import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Calendar, Users, Settings, LogOut, Building2,
  Menu, X, QrCode, ChevronRight, Home, Plus, Disc3, Briefcase, Mail, Newspaper, Bell, UserCheck, ArrowLeft, DollarSign, Shield, Lock, Map
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useProducerAuth } from '../../context/ProducerAuthContext';
import { SubscriptionBannerSmall } from './SubscriptionBanner';
import ProducerDialNav from './ProducerDialNav';

// Full menu for producers with required permissions
const PRODUCER_MENU_ITEMS = [
  { path: '/producer/dashboard', icon: LayoutDashboard, label: 'Dashboard', shortLabel: 'Inicio', permissions: [] }, // No permission required
  { path: '/producer/events', icon: Calendar, label: 'Mis Eventos', shortLabel: 'Eventos', permissions: ['manage_events', 'manage_tickets'] },
  { path: '/producer/venues', icon: Map, label: 'Mis Venues', shortLabel: 'Venues', permissions: ['manage_events'] },
  { path: '/producer/press', icon: Newspaper, label: 'Servicios', shortLabel: 'Servicios', hasBadge: 'press', permissions: [] },
  { path: '/producer/settlements', icon: DollarSign, label: 'Liquidaciones', shortLabel: 'Liquidaciones', permissions: ['manage_settlements', 'view_reports'] },
  { path: '/producer/djs', icon: Disc3, label: 'DJs Sugeridos', shortLabel: 'DJs', hasBadge: 'djs', permissions: [] },
  { path: '/producer/jobs', icon: Briefcase, label: 'Portal Empleo', shortLabel: 'Empleo', permissions: [] },
  { path: '/producer/email-metrics', icon: Mail, label: 'Métricas Email', shortLabel: 'Emails', permissions: ['view_reports'] },
  { path: '/producer/community', icon: Users, label: 'Comunidad', shortLabel: 'Comunidad', permissions: [] },
  { path: '/producer/admins', icon: Shield, label: 'Administradores', shortLabel: 'Admins', permissions: ['manage_admins'] },
  { path: '/producer/validators', icon: UserCheck, label: 'Validadores', shortLabel: 'Validadores', permissions: ['manage_validators'] },
  { path: '/producer/scanner', icon: QrCode, label: 'Escanear QR', shortLabel: 'Escanear', permissions: ['scan_tickets'] },
  { path: '/producer/settings', icon: Settings, label: 'Configuración', shortLabel: 'Config', permissions: [] },
];

// Limited menu for validators (dashboard + scanner)
const VALIDATOR_MENU_ITEMS = [
  { path: '/producer/validator-dashboard', icon: LayoutDashboard, label: 'Dashboard', shortLabel: 'Inicio', permissions: [] },
  { path: '/producer/scanner', icon: QrCode, label: 'Escanear QR', shortLabel: 'Escanear', permissions: [] },
];

// Items principales para el bottom nav móvil (solo 5)
const PRODUCER_MOBILE_NAV_ITEMS = [
  { path: '/producer/dashboard', icon: LayoutDashboard, label: 'Inicio', permissions: [] },
  { path: '/producer/events', icon: Calendar, label: 'Eventos', permissions: ['manage_events', 'manage_tickets'] },
  { path: '/producer/press', icon: Newspaper, label: 'Servicios', hasBadge: 'press', permissions: [] },
  { path: '/producer/scanner', icon: QrCode, label: 'Escanear', permissions: ['scan_tickets'] },
  { path: '/producer/settings', icon: Settings, label: 'Más', permissions: [] },
];

// Mobile nav for validators
const VALIDATOR_MOBILE_NAV_ITEMS = [
  { path: '/producer/validator-dashboard', icon: LayoutDashboard, label: 'Inicio', permissions: [] },
  { path: '/producer/scanner', icon: QrCode, label: 'Escanear', permissions: [] },
];

// Logo del toro solo
const BRAVO_LOGO = 'https://customer-assets.emergentagent.com/job_rebill-payment-fix/artifacts/vodl6hhv_Blanco-Icono.png';

// Desktop Sidebar Item with permission support
const SidebarItem = memo(({ item, isActive, collapsed, badge, hasPermission = true }) => {
  const Icon = item.icon;
  const isDisabled = !hasPermission;
  
  if (isDisabled) {
    return (
      <div
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-not-allowed opacity-40 grayscale group relative"
        title="No tenés permiso para acceder a esta sección"
      >
        <div className="relative">
          <Icon size={20} className="text-zinc-500" />
          <Lock size={10} className="absolute -bottom-1 -right-1 text-zinc-600" />
        </div>
        {!collapsed && (
          <span className="font-medium text-sm flex-1 text-zinc-500">{item.label}</span>
        )}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
            {item.label} (Sin permiso)
          </div>
        )}
      </div>
    );
  }
  
  return (
    <Link
      to={item.path}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative ${
        isActive 
          ? 'bg-gradient-to-r from-violet-600/20 to-pink-600/10 text-violet-400 border-l-2 border-pink-500' 
          : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
      }`}
    >
      <div className="relative">
        <Icon size={20} className={isActive ? 'text-violet-400' : 'group-hover:text-white'} />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-pink-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      {!collapsed && (
        <span className="font-medium text-sm flex-1">{item.label}</span>
      )}
      {!collapsed && badge > 0 && (
        <span className="bg-pink-500/20 text-pink-400 text-xs font-bold px-1.5 py-0.5 rounded">
          {badge}
        </span>
      )}
      {collapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
          {item.label}
          {badge > 0 && <span className="ml-1 text-pink-400">({badge})</span>}
        </div>
      )}
    </Link>
  );
});

// Mobile Bottom Nav Item with permission support
const MobileNavItem = memo(({ item, isActive, badge, hasPermission = true }) => {
  const Icon = item.icon;
  const isDisabled = !hasPermission;
  
  if (isDisabled) {
    return (
      <div className="flex flex-col items-center justify-center py-1.5 px-2 flex-1 relative opacity-40 grayscale cursor-not-allowed">
        <div className="relative flex flex-col items-center gap-0.5">
          <div className="relative">
            <Icon size={20} className="text-zinc-500" />
            <Lock size={8} className="absolute -bottom-0.5 -right-0.5 text-zinc-600" />
          </div>
          <span className="text-[10px] font-medium text-zinc-500 leading-tight">{item.label}</span>
        </div>
      </div>
    );
  }
  
  return (
    <Link
      to={item.path}
      className="flex flex-col items-center justify-center py-1.5 px-2 flex-1 relative group"
    >
      <motion.div
        whileTap={{ scale: 0.9 }}
        className={`relative flex flex-col items-center gap-0.5 transition-colors duration-200 ${
          isActive ? 'text-violet-400' : 'text-zinc-500'
        }`}
      >
        {/* Active indicator bar */}
        {isActive && (
          <motion.div
            layoutId="producerNavIndicator"
            className="absolute -top-1.5 w-6 h-0.5 bg-gradient-to-r from-violet-500 to-pink-500 rounded-full"
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          />
        )}
        
        <div className="relative">
          <Icon 
            size={22} 
            strokeWidth={isActive ? 2.5 : 1.5} 
            className={`transition-transform duration-200 ${isActive ? 'scale-105' : ''}`}
          />
          {badge > 0 && (
            <span className="absolute -top-1 -right-1.5 min-w-[14px] h-[14px] bg-pink-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </div>
        
        <span className={`text-[9px] mt-0.5 font-medium uppercase tracking-wide ${
          isActive ? 'text-violet-600 font-semibold' : 'text-zinc-500'
        }`}>
          {item.label}
        </span>
      </motion.div>
    </Link>
  );
});

const ProducerLayout = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasAnyPermission, isSecondaryAdmin, loading: authLoading, permissions } = useProducerAuth();
  const [producer, setProducer] = useState(() => {
    const cached = localStorage.getItem('producer_data');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(!producer);
  const [badges, setBadges] = useState({
    press: 0, // pending quotes, contracts
    events: 0,
    jobs: 0,
    djs: 0 // total approved DJs
  });
  const hasLoadedRef = React.useRef(false);
  
  // Permissions are ready when auth is not loading and permissions are loaded
  const permissionsReady = !authLoading && permissions !== null;
  
  // Check if current user is a validator
  const isValidator = producer?.is_validator === true;
  
  // Select menu items based on user type
  const MENU_ITEMS = isValidator ? VALIDATOR_MENU_ITEMS : PRODUCER_MENU_ITEMS;
  const MOBILE_NAV_ITEMS = isValidator ? VALIDATOR_MOBILE_NAV_ITEMS : PRODUCER_MOBILE_NAV_ITEMS;

  // Function to check if user has permission for a menu item
  const checkMenuPermission = (item) => {
    // If no permissions required, always allow
    if (!item.permissions || item.permissions.length === 0) return true;
    // If user has any of the required permissions, allow
    return hasAnyPermission(item.permissions);
  };

  // Redirect validators to their dashboard if they try to access producer-only pages
  useEffect(() => {
    if (isValidator) {
      const validatorAllowedPaths = ['/producer/validator-dashboard', '/producer/scanner'];
      const isAllowedPath = validatorAllowedPaths.some(path => location.pathname.startsWith(path));
      
      if (!isAllowedPath) {
        navigate('/producer/validator-dashboard', { replace: true });
      }
    }
  }, [isValidator, location.pathname, navigate]);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    
    const token = localStorage.getItem('producer_token');
    if (!token) {
      navigate('/producer/login', { replace: true });
      return;
    }
    
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    if (producer) {
      setLoading(false);
      refreshProducerData();
    } else {
      fetchProducerData();
    }
    
    // Fetch badge counts
    fetchBadgeCounts();
  }, []);

  const fetchBadgeCounts = async () => {
    try {
      const [quotesRes, offersRes, contractsRes, djsRes] = await Promise.all([
        api.get('/press/quotes/producer').catch(() => ({ data: [] })),
        api.get('/press/offers/producer').catch(() => ({ data: [] })),
        api.get('/press/contracts/producer').catch(() => ({ data: [] })),
        api.get('/dj/browse?limit=1').catch(() => ({ data: { total: 0 } }))
      ]);
      
      // Count pending/quoted (not yet accepted/rejected) quotes
      const quotes = Array.isArray(quotesRes.data) ? quotesRes.data : [];
      const pendingQuotes = quotes.filter(q => q.status === 'pending' || q.status === 'quoted').length;
      
      // Count pending offers
      const offers = Array.isArray(offersRes.data) ? offersRes.data : [];
      const pendingOffers = offers.filter(o => o.status === 'pending').length;
      
      // Count active contracts
      const contracts = Array.isArray(contractsRes.data) ? contractsRes.data : [];
      const activeContracts = contracts.filter(c => c.status === 'active').length;
      
      // Get total DJs count
      const djsTotal = djsRes.data?.total || 0;
      
      setBadges(prev => ({
        ...prev,
        press: pendingQuotes + pendingOffers + activeContracts,
        djs: djsTotal
      }));
    } catch (error) {
      console.error('Error fetching badge counts:', error);
    }
  };

  const refreshProducerData = useCallback(async () => {
    try {
      const response = await api.get('/producers/me');
      setProducer(response.data);
      localStorage.setItem('producer_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Background refresh error:', error);
    }
  }, []);

  const fetchProducerData = useCallback(async () => {
    try {
      const response = await api.get('/producers/me');
      setProducer(response.data);
      localStorage.setItem('producer_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('producer_token');
      localStorage.removeItem('producer_data');
      navigate('/producer/login', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('producer_token');
    localStorage.removeItem('producer_data');
    toast.success('Sesión cerrada');
    navigate('/producer/login', { replace: true });
  }, [navigate]);

  const handleBackToAccount = useCallback(() => {
    navigate('/mi-cuenta');
  }, [navigate]);

  const isActive = useCallback((path) => {
    if (path === '/producer/events') {
      return location.pathname === path || location.pathname.startsWith('/producer/events/');
    }
    return location.pathname === path;
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full bg-zinc-950 border-r border-zinc-800/50 transition-all duration-300 z-40 ${
          sidebarCollapsed ? 'w-[72px]' : 'w-60'
        }`}
      >
        {/* Logo Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800/50">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAVO_LOGO} alt="Bravo" className="h-8 w-8 object-contain" />
            {!sidebarCollapsed && (
              <span className="font-bold text-white text-sm">PRODUCTORAS</span>
            )}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <ChevronRight size={16} className={`transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Producer Card */}
        <div className={`p-3 border-b border-zinc-800/50 ${sidebarCollapsed ? 'px-2' : ''}`}>
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            {producer?.branding?.logo_url ? (
              <img 
                src={producer.branding.logo_url} 
                alt={producer.business_name}
                className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-9 h-9 bg-gradient-to-br from-violet-600/30 to-violet-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building2 className="text-violet-600" size={18} />
              </div>
            )}
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">{producer?.business_name}</p>
                {producer?.is_verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    Verificada
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {MENU_ITEMS.map((item) => (
            <SidebarItem
              key={item.path}
              item={item}
              isActive={isActive(item.path)}
              collapsed={sidebarCollapsed}
              badge={item.hasBadge ? badges[item.hasBadge] : 0}
              hasPermission={checkMenuPermission(item)}
            />
          ))}
        </nav>

        {/* Create Event Button - Only for producers with permission, not validators */}
        {!sidebarCollapsed && !isValidator && hasPermission('manage_events') && (
          <div className="p-3 border-t border-zinc-800/50">
            <Link
              to="/producer/events/create"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-violet-600 to-pink-600 text-white font-semibold text-sm rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/20"
            >
              <Plus size={18} />
              Crear Evento
            </Link>
          </div>
        )}
        
        {/* Disabled Create Event Button for admins without permission */}
        {!sidebarCollapsed && !isValidator && !hasPermission('manage_events') && isSecondaryAdmin && (
          <div className="p-3 border-t border-zinc-800/50">
            <div
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-zinc-800 text-zinc-500 font-semibold text-sm rounded-lg cursor-not-allowed opacity-50 grayscale"
              title="No tenés permiso para crear eventos"
            >
              <Lock size={14} />
              <Plus size={18} />
              Crear Evento
            </div>
          </div>
        )}

        {/* Super Productora Subscription Banner */}
        {!sidebarCollapsed && !isValidator && (
          <div className="p-3 border-t border-zinc-800/50">
            <SubscriptionBannerSmall />
          </div>
        )}

        {/* Back to Account */}
        <div className={`p-3 border-t border-zinc-800/50 ${sidebarCollapsed ? 'px-2' : ''}`}>
          <button
            onClick={handleBackToAccount}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-violet-500/10 hover:text-violet-400 transition-colors w-full ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
          >
            <ArrowLeft size={18} />
            {!sidebarCollapsed && <span className="text-sm font-medium">Mi Cuenta</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Navigation - Camera dial wheel with ALL sections */}
      <ProducerDialNav
        items={MENU_ITEMS}
        badges={badges}
        checkPermission={checkMenuPermission}
      />

      {/* Mobile Slide-out Menu (accessible via swipe or programmatically) */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-zinc-950 z-50 flex flex-col"
            >
              {/* Mobile Menu Header */}
              <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800/50">
                <div className="flex items-center gap-2">
                  <img src={BRAVO_LOGO} alt="Bravo" className="h-7 w-7 object-contain" />
                  <span className="font-bold text-white text-sm">PRODUCTORAS</span>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 -mr-2 text-zinc-400 hover:text-white"
                >
                  <X size={22} />
                </button>
              </div>
              
              {/* Producer Card */}
              <div className="p-4 border-b border-zinc-800/50">
                <div className="flex items-center gap-3">
                  {producer?.branding?.logo_url ? (
                    <img 
                      src={producer.branding.logo_url} 
                      alt={producer.business_name}
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-violet-600/30 to-violet-600/10 rounded-xl flex items-center justify-center">
                      <Building2 className="text-violet-600" size={24} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{producer?.business_name}</p>
                    <p className="text-xs text-zinc-500 truncate">{producer?.email}</p>
                    {producer?.is_verified && (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-400 mt-1">
                        <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                        Verificada
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Create Event Button - Only if has permission */}
              {hasPermission('manage_events') ? (
                <div className="p-4">
                  <Link
                    to="/producer/events/create"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-violet-600 to-pink-600 text-black font-bold rounded-xl"
                  >
                    <Plus size={20} />
                    Crear Evento
                  </Link>
                </div>
              ) : isSecondaryAdmin ? (
                <div className="p-4">
                  <div className="flex items-center justify-center gap-2 w-full py-3 bg-zinc-800 text-zinc-500 font-bold rounded-xl cursor-not-allowed opacity-50 grayscale">
                    <Lock size={14} />
                    <Plus size={20} />
                    Crear Evento
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <Link
                    to="/producer/events/create"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-violet-600 to-pink-600 text-black font-bold rounded-xl"
                  >
                    <Plus size={20} />
                    Crear Evento
                  </Link>
                </div>
              )}
              
              {/* Navigation */}
              <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
                {MENU_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const badge = item.hasBadge ? badges[item.hasBadge] : 0;
                  const itemHasPermission = checkMenuPermission(item);
                  
                  if (!itemHasPermission) {
                    return (
                      <div
                        key={item.path}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl opacity-40 grayscale cursor-not-allowed"
                        title="No tenés permiso para acceder a esta sección"
                      >
                        <div className="relative">
                          <Icon size={20} className="text-zinc-500" />
                          <Lock size={10} className="absolute -bottom-0.5 -right-0.5 text-zinc-600" />
                        </div>
                        <span className="font-medium flex-1 text-zinc-500">{item.label}</span>
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                        active 
                          ? 'bg-violet-600/20 text-violet-600' 
                          : 'text-zinc-400 hover:bg-zinc-800'
                      }`}
                    >
                      <div className="relative">
                        <Icon size={20} />
                        {badge > 0 && (
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                            {badge > 9 ? '9+' : badge}
                          </span>
                        )}
                      </div>
                      <span className="font-medium flex-1">{item.label}</span>
                      {badge > 0 && (
                        <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2 py-0.5 rounded">
                          {badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
              
              {/* Back to Bravo */}
              <div className="p-3 border-t border-zinc-800/50">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:bg-zinc-800 transition-colors"
                >
                  <Home size={20} />
                  <span className="font-medium">Volver a Bravo</span>
                </Link>
              </div>
              
              {/* Back to Account */}
              <div className="p-3 border-t border-zinc-800/50">
                <button
                  onClick={handleBackToAccount}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-zinc-400 hover:bg-violet-500/10 hover:text-violet-400 transition-colors w-full"
                >
                  <ArrowLeft size={20} />
                  <span className="font-medium">Mi Cuenta</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`min-h-screen pt-4 pb-28 lg:pt-0 lg:pb-0 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-[72px]' : 'lg:pl-60'
      }`}>
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default ProducerLayout;
